import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, FolderKanban, FileSignature, Lock, Sparkles, ChevronLeft, ChevronRight, Check, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "contact",  label: "Company",   icon: Building2,    hint: "Who they are" },
  { id: "project",  label: "Engagement",icon: FolderKanban, hint: "Project + value" },
  { id: "papers",   label: "Paperwork", icon: FileSignature,hint: "NDA / MSA / SOW" },
  { id: "vault",    label: "Access",    icon: Lock,         hint: "Portal + vault" },
  { id: "welcome",  label: "Welcome",   icon: Sparkles,     hint: "Send the invite" },
];

const blank = () => ({
  email: "", name: "", company: "", title: "Founder", phone: "", country: "India",
  tag: "new", source: "",
  project_id: "", project_name: "", business_unit: "BU1",
  engagement_type: "concept_design", currency: "INR", estimated_value: "",
  contracts: ["NDA"],
  visible_project_ids: [],
  kickoff_when: "", welcome_note: "",
});

export default function ClientOnboardingWizard({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blank);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0); setForm(blank());
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = () => {
    if (step === 0 && (!form.email.trim() || !form.name.trim())) return "Email + name required";
    return null;
  };

  const next = () => { const e = valid(); if (e) { toast.error(e); return; } setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleContract = (t) => set("contracts", form.contracts.includes(t) ? form.contracts.filter((x) => x !== t) : [...form.contracts, t]);
  const toggleVisible  = (pid) => set("visible_project_ids", form.visible_project_ids.includes(pid)
    ? form.visible_project_ids.filter((x) => x !== pid) : [...form.visible_project_ids, pid]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : 0,
        project_id: form.project_id || null,
        project_name: form.project_id ? null : (form.project_name || null),
      };
      const { data } = await api.post("/onboarding/client", payload);
      toast.success(`${data.party.name} invited. ${data.contracts.length} contract(s) drafted.`);
      onOpenChange(false); onCreated && onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const bodies = {
    contact: (
      <div className="space-y-3" data-testid="cob-contact">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="label-overline">Company</Label><Input className="rounded-sm mt-1" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Co" data-testid="cob-company" /></div>
          <div><Label className="label-overline">Contact name *</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cob-name" /></div>
          <div><Label className="label-overline">Email *</Label><Input className="rounded-sm mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="cob-email" /></div>
          <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="cob-title" /></div>
          <div><Label className="label-overline">Phone</Label><Input className="rounded-sm mt-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><Label className="label-overline">Country</Label><Input className="rounded-sm mt-1" value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
          <div>
            <Label className="label-overline">Tag</Label>
            <Select value={form.tag} onValueChange={(v) => set("tag", v)}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="repeat">Repeat</SelectItem><SelectItem value="blacklist">Blacklist</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="label-overline">Source</Label><Input className="rounded-sm mt-1" value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Referral, inbound, LinkedIn…" /></div>
        </div>
      </div>
    ),
    project: (
      <div className="space-y-3" data-testid="cob-project">
        <div>
          <Label className="label-overline">Link existing project</Label>
          <Select value={form.project_id || "__new"} onValueChange={(v) => set("project_id", v === "__new" ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="cob-project-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__new">+ Create a new project shell</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.business_unit}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!form.project_id && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="label-overline">New project name</Label><Input className="rounded-sm mt-1" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="e.g. Acme Smart Bottle" data-testid="cob-new-project-name" /></div>
            <div>
              <Label className="label-overline">Business unit</Label>
              <Select value={form.business_unit} onValueChange={(v) => set("business_unit", v)}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Strategy">Strategy</SelectItem><SelectItem value="BU1">BU1</SelectItem><SelectItem value="BU2">BU2</SelectItem><SelectItem value="BU3">BU3</SelectItem><SelectItem value="Operations">Operations</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="label-overline">Engagement type</Label>
            <Select value={form.engagement_type} onValueChange={(v) => set("engagement_type", v)}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="concept_design">Concept design</SelectItem>
                <SelectItem value="mvp">MVP</SelectItem>
                <SelectItem value="dfm">DFM</SelectItem>
                <SelectItem value="launch">Launch</SelectItem>
                <SelectItem value="retainer">Retainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="INR">₹ INR</SelectItem><SelectItem value="USD">$ USD</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="label-overline">Estimated value</Label><Input type="number" className="rounded-sm mt-1" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="cob-value" /></div>
        </div>
      </div>
    ),
    papers: (
      <div className="space-y-3" data-testid="cob-papers">
        <p className="text-xs text-slate-500">Picked contracts get drafted and sent to the client when they accept their invite. Track them in CRM → Contracts.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {["NDA", "MSA", "SOW"].map((t) => {
            const picked = form.contracts.includes(t);
            return (
              <button key={t} onClick={() => toggleContract(t)} className={`p-3 rounded-sm border-2 text-left transition-colors ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200 hover:border-slate-300"}`} data-testid={`cob-contract-${t}`}>
                <div className="font-display font-bold text-sm">{t}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{t === "NDA" ? "Confidentiality" : t === "MSA" ? "Master agreement" : "Scope + value"}</div>
              </button>
            );
          })}
        </div>
      </div>
    ),
    vault: (
      <div className="space-y-3" data-testid="cob-vault">
        <p className="text-xs text-slate-500">A My Vault folder is created automatically. Pick which projects the client can see in their portal.</p>
        <div className="space-y-1 max-h-[260px] overflow-y-auto pr-1">
          {projects.map((p) => {
            const picked = form.visible_project_ids.includes(p.id) || form.project_id === p.id;
            return (
              <label key={p.id} className={`flex items-center gap-2 px-3 py-2 rounded-sm border-2 ${picked ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200"}`}>
                <input type="checkbox" checked={picked} onChange={() => toggleVisible(p.id)} data-testid={`cob-visible-${p.id}`} />
                <span className="text-xs">{p.name}</span>
                <span className="text-[10px] text-slate-400 ml-auto">{p.business_unit}</span>
              </label>
            );
          })}
        </div>
      </div>
    ),
    welcome: (
      <div className="space-y-3" data-testid="cob-welcome">
        <div className="card-flat p-3 bg-[#F54800]/5 border-l-4 border-l-[#F54800]">
          <div className="text-xs font-display font-bold text-slate-900 mb-1">About to create:</div>
          <ul className="text-[11px] text-slate-700 space-y-0.5">
            <li>• Client account · {form.name} ({form.email}) — invited via magic-link</li>
            <li>• {form.project_id ? "Link to existing project" : (form.project_name ? `New project "${form.project_name}"` : "No project yet")}</li>
            <li>• {form.contracts.length} contract(s) drafted: {form.contracts.join(", ") || "—"}</li>
            <li>• Vault folder + {form.visible_project_ids.length || (form.project_id ? 1 : (form.project_name ? 1 : 0))} visible project(s)</li>
          </ul>
        </div>
        <div>
          <Label className="label-overline">Welcome note (optional)</Label>
          <Textarea rows={3} className="rounded-sm mt-1 resize-none" placeholder="Anything personal you want them to see in the welcome email…" value={form.welcome_note} onChange={(e) => set("welcome_note", e.target.value)} />
        </div>
        <div>
          <Label className="label-overline">Optional kickoff meeting</Label>
          <Input type="datetime-local" className="rounded-sm mt-1" value={form.kickoff_when} onChange={(e) => set("kickoff_when", e.target.value)} />
        </div>
      </div>
    ),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] flex flex-col p-0" data-testid="client-onboarding-wizard">
        <DialogHeader className="p-5 border-b border-slate-100 shrink-0"><DialogTitle className="font-display text-lg">Onboard client · {STEPS[step].label}</DialogTitle><DialogDescription className="sr-only">Multi-step wizard to onboard a new client: company, engagement, paperwork, vault access, and welcome.</DialogDescription></DialogHeader>
        <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 overflow-x-auto shrink-0">
          {STEPS.map((s, i) => {
            const Icon = s.icon; const active = i === step; const done = i < step;
            return (
              <button key={s.id} onClick={() => i <= step && setStep(i)} className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-[11px] font-semibold ${active ? "bg-slate-900 text-white" : done ? "text-[#F54800]" : "text-slate-400"}`} data-testid={`cob-step-${s.id}`}>
                {done ? <Check size={11} /> : <Icon size={11} />}<span>{s.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          <p className="text-[11px] text-slate-500 mb-4">{STEPS[step].hint}</p>
          {bodies[STEPS[step].id]}
        </div>
        <div className="flex items-center justify-between p-4 border-t border-slate-100 shrink-0">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-sm"><ChevronLeft size={13} className="mr-1" /> Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="cob-next">Next <ChevronRight size={13} className="ml-1" /></Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="cob-submit">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}Create &amp; send invite
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
